import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { Input, Dropdown, Div, Icon, Text, Switch } from '@pinpoint/design-kit';

import Rbac from 'src/containers/rbac';
import CONSTANTS from 'src/store/constants';

import BaseDialog from './base';

export function CreateClassifierModel({ isOpen, actions }) {
  const [typeSelectOpen, setTypeSelectOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const defaultValues = {
    name: '',
    petsPercentage: null,
    predictionType: '',
    typeDetail: '',
    hidden: false,
    training: false,
  };

  const { handleSubmit, errors, control, getValues } = useForm({ defaultValues });
  const onSubmit = (data) => {
    const inputData = data;
    inputData.name = inputData.name.trim();
    inputData.petsPercentage = Number(inputData.petsPercentage);
    if (inputData.hidden) inputData.hiddenByRole = 'ADMIN';
    else inputData.hiddenByRole = null;
    delete inputData.hidden;
    if (inputData.training) inputData.trainingType = 'transformer';
    else inputData.trainingType = 'catboost';
    delete inputData.training;
    actions.handlers[0].on(data);
    actions.close.on();
  };
  const formSubmit = handleSubmit(onSubmit);

  const handleSettingsToggle = useCallback(() => {
    setIsSettingsOpen(!isSettingsOpen);
  }, [isSettingsOpen]);

  useEffect(() => {
    if (isOpen) {
      setIsSettingsOpen(false);
    }
  }, [isOpen]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <BaseDialog
      isOpen={isOpen}
      hideCloseIcon
      actions={{
        ...actions,
        handlers: [
          {
            on: formSubmit,
          },
        ],
      }}
      items={{
        buttons: [
          {
            text: 'CREATE',
            textSize: 'body',
            close: false,
          },
        ],
        content: [
          <Div key="input-0" d="flex" flexDir="column">
            <Text textSize="input" textWeight="500" textColor="shade66">
              Select Prediction Type
            </Text>
            <Controller
              name="predictionType"
              render={({ onChange, onBlur, value, name, ref }) => (
                <Dropdown
                  onBlur={onBlur}
                  value={value}
                  name={name}
                  ref={ref}
                  defaultValue={<Text />}
                  isOpen={typeSelectOpen}
                  onClick={() => setTypeSelectOpen(!typeSelectOpen)}
                  onSelect={(idx) => onChange(CONSTANTS.TAL_MODEL.TYPES.slice(1)[idx])}
                  menu={CONSTANTS.TAL_MODEL.TYPES.slice(1)}
                  borderColor="shade66"
                  w="100%"
                  h="1.875rem"
                  p={{ r: '0px', l: '.5rem' }}
                  m={{ y: '0.25rem' }}
                  openSuffix={
                    <Div
                      d="flex"
                      justify="center"
                      align="center"
                      h="1.875rem"
                      w="1.875rem"
                      borderColor="shade66"
                      border={{ l: '1px solid' }}
                    >
                      <Icon viewBox="0 0 50 50" name="UpArrow" size=".625rem" color="shade66" />
                    </Div>
                  }
                  closeSuffix={
                    <Div
                      d="flex"
                      justify="center"
                      align="center"
                      h="1.875rem"
                      w="1.875rem"
                      border={{ l: '1px solid' }}
                      borderColor="shade66"
                    >
                      <Icon viewBox="0 0 50 50" name="DownArrow" size=".625rem" color="shade66" />
                    </Div>
                  }
                />
              )}
              control={control}
              rules={{
                required: 'Model prediction type is required',
              }}
            />
            <ErrorMessage
              errors={errors}
              name="predictionType"
              render={({ message }) => {
                if (![undefined, null, ''].includes(message)) {
                  return (
                    <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                      {message}
                    </Text>
                  );
                }
                return null;
              }}
            />
          </Div>,
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>
            {{
              'Early Cancellation': (
                <Div d="flex" flexDir="column" m={{ t: '1.25rem' }}>
                  <Text textWeight="500" textSize="input" textColor="shade66">
                    Cancellation window in days
                  </Text>
                  <Controller
                    m={{ t: '.25rem' }}
                    borderColor="shade66"
                    name="typeDetail"
                    as={Input}
                    control={control}
                    rules={{
                      required: 'Cancellation window is required',
                    }}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="typeDetail"
                    render={({ message }) => {
                      if (![undefined, null, ''].includes(message)) {
                        return (
                          <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                            {message}
                          </Text>
                        );
                      }
                      return null;
                    }}
                  />
                </Div>
              ),
              'Premium Leakage': (
                <Div d="flex" flexDir="column" m={{ t: '1.25rem' }}>
                  <Text textWeight="500" textSize="input" textColor="shade66">
                    Subtype
                  </Text>
                  <Controller
                    m={{ t: '.25rem' }}
                    borderColor="shade66"
                    name="typeDetail"
                    as={Input}
                    control={control}
                    rules={{
                      required: 'Subtype is required',
                    }}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="typeDetail"
                    render={({ message }) => {
                      if (![undefined, null, ''].includes(message)) {
                        return (
                          <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                            {message}
                          </Text>
                        );
                      }
                      return null;
                    }}
                  />
                </Div>
              ),
              Other: (
                <Div d="flex" flexDir="column" m={{ t: '1.25rem' }}>
                  <Text textWeight="500" textSize="input" textColor="shade66">
                    Please describe
                  </Text>
                  <Controller
                    m={{ t: '.25rem' }}
                    borderColor="shade66"
                    name="typeDetail"
                    as={Input}
                    control={control}
                    rules={{
                      required: 'Description is required',
                    }}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="typeDetail"
                    render={({ message }) => {
                      if (![undefined, null, ''].includes(message)) {
                        return (
                          <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                            {message}
                          </Text>
                        );
                      }
                      return null;
                    }}
                  />
                </Div>
              ),
            }[getValues('predictionType')] || null}
          </>,
          <Div key="input-2" d="flex" flexDir="column">
            <Text textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
              Enter the name of your model
            </Text>
            <Controller
              borderColor="shade66"
              name="name"
              as={Input}
              control={control}
              rules={{
                required: 'Name is required',
                pattern: {
                  value: /^[^0-9\s\W]/,
                  message: 'Name must start with a letter - not a number, special character, or space.',
                },
              }}
              m={{ t: '.25rem' }}
              h="1.875rem"
            />
            <ErrorMessage
              errors={errors}
              name="name"
              render={({ message }) => {
                if (![undefined, null, ''].includes(message)) {
                  return (
                    <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                      {message}
                    </Text>
                  );
                }
                return null;
              }}
            />
          </Div>,
          <Div key="input-3" d="flex" flexDir="row">
            <Div>
              <Div>
                <Text d="inline-block" textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
                  Target behavior frequency as a percentage
                </Text>
                <Icon
                  viewBox="0 0 24 24"
                  d="inline-block"
                  name="InfoSerif"
                  m={{ x: '0.5rem' }}
                  style={{ verticalAlign: 'text-bottom' }}
                  size=".875rem"
                  data-tip="Please enter the proportion of risk-related records, as a percentage of the population where the prediction will be implemented. For example, if you are predicting early cancellation at point of quote, and 18.8% of customers cancel early, then please enter '18.8' in this field."
                />
              </Div>
              <Controller
                name="petsPercentage"
                as={Input}
                m={{ t: '.25rem' }}
                rounded={{ l: 'md' }}
                h="1.875rem"
                type="number"
                borderColor="shade66"
                suffix={
                  <Div
                    h="1.875rem"
                    w="1.375rem"
                    border={{ y: '1px solid', r: '1px solid' }}
                    borderColor="shade66"
                    rounded={{ tr: 'md', br: 'md' }}
                    p={{ x: '0.25rem' }}
                    align="center"
                    justify="center"
                    d="flex"
                  >
                    <Icon m="0px" name="Percent" color="grey" size=".75rem" viewBox="0 0 12 12" />
                  </Div>
                }
                control={control}
                rules={{
                  required: 'HIT % is required',
                  min: { value: 0, message: 'The HIT % cannot be lesser than 0%' },
                  max: { value: 100, message: 'The HIT % cannot be greater than 100%' },
                }}
              />
              <ErrorMessage
                errors={errors}
                name="petsPercentage"
                render={({ message }) => {
                  if (![undefined, null, ''].includes(message)) {
                    return (
                      <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                        {message}
                      </Text>
                    );
                  }
                  return null;
                }}
              />
            </Div>
          </Div>,
          <Div key="input-4" d="flex" flexDir="column">
            <Rbac roles={CONSTANTS.ROLES.slice(0, 3)}>
              <Div m={{ t: '1.5rem' }} d="flex" flexDir="column" data-debug="settings-container">
                <Div d="flex" flexDir="row" data-debug="settings-sub-container">
                  <Div d="flex" align="center" justify="center" flexWrap="nowrap" data-debug="settings-toggle">
                    <Text
                      d="flex"
                      align="center"
                      justify="center"
                      textSize="input"
                      textWeight="500"
                      textColor="shade66"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Advanced Settings
                    </Text>
                    <Div onClick={handleSettingsToggle} cursor="pointer">
                      <Switch
                        checked={isSettingsOpen}
                        // @ts-ignore
                        d="inline-block"
                        m={{ x: '.5rem' }}
                      />
                    </Div>
                  </Div>
                  {isSettingsOpen && (
                    <Div d="flex" align="center" data-debug="settings">
                      <Controller
                        name="hidden"
                        h="1.875rem"
                        type="checkbox"
                        control={control}
                        render={({ onChange, value }) => (
                          <Div
                            d="flex"
                            p={{ l: '1rem' }}
                            m={{ l: '0.5rem' }}
                            style={{ borderLeft: '1px black solid' }}
                            align="center"
                            justify="center"
                            flexDir="row-reverse"
                          >
                            <Text textSize="input" textWeight="500" textColor="shade66" d="inline-block">
                              Hidden
                            </Text>
                            <Div onClick={() => onChange(!value)} cursor="pointer">
                              <Switch
                                checked={value}
                                // @ts-ignore
                                d="inline-block"
                                m={{ x: '.5rem' }}
                              />
                            </Div>
                            <Text textSize="input" textWeight="500" textColor="shade66" d="inline-block">
                              Visible
                            </Text>
                          </Div>
                        )}
                      />
                      <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN]}>
                        <Controller
                          name="training"
                          h="1.875rem"
                          type="checkbox"
                          control={control}
                          render={({ onChange, value }) => (
                            <Div
                              d="flex"
                              p={{ l: '1rem' }}
                              m={{ l: '0.5rem' }}
                              style={{ borderLeft: '1px black solid' }}
                              align="center"
                              justify="center"
                              flexDir="row-reverse"
                            >
                              <Text textSize="input" textWeight="500" textColor="shade66" d="inline-block">
                                Deep Learning
                              </Text>
                              <Div onClick={() => onChange(!value)} cursor="pointer">
                                <Switch
                                  checked={value}
                                  // @ts-ignore
                                  d="inline-block"
                                  m={{ x: '.5rem' }}
                                />
                              </Div>
                              <Text textSize="input" textWeight="500" textColor="shade66" d="inline-block">
                                CatBoost
                              </Text>
                            </Div>
                          )}
                        />
                      </Rbac>
                    </Div>
                  )}
                </Div>
                <ErrorMessage
                  errors={errors}
                  name="internalValidationPercentage"
                  render={({ message }) => {
                    if (![undefined, null, ''].includes(message)) {
                      return (
                        <Text textColor="danger800" textSize="caption" m={{ t: '0.25rem' }}>
                          {message}
                        </Text>
                      );
                    }
                    return null;
                  }}
                />
              </Div>
            </Rbac>
          </Div>,
        ],
      }}
      title={{
        text: 'Generate Classifier Model',
        backgroundColor: 'talLime',
        color: 'white',
        textSize: 'title',
        icon: (
          <Icon
            name="ClassifierModel"
            viewBox="0 0 10.46 16"
            size="2.375rem"
            color="white"
            alignSelf="center"
            justifySelf="center"
          />
        ),
      }}
    />
  );
}

CreateClassifierModel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    cancel: PropTypes.shape({
      before: PropTypes.func,
      on: PropTypes.func.isRequired,
      after: PropTypes.func,
    }).isRequired,
    close: PropTypes.shape({
      before: PropTypes.func,
      on: PropTypes.func.isRequired,
      after: PropTypes.func,
    }).isRequired,
    handlers: PropTypes.arrayOf(
      PropTypes.shape({
        before: PropTypes.func,
        on: PropTypes.func.isRequired,
        after: PropTypes.func,
      })
    ),
  }).isRequired,
  // eslint-disable-next-line react/require-default-props
  title: PropTypes.shape({
    text: PropTypes.string.isRequired,
    textSize: PropTypes.string,
    backgroundColor: PropTypes.string.isRequired,
    color: PropTypes.string,
  }),
};

export default CreateClassifierModel;
